import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "aos/dist/aos.css";
import { Gift, ShieldCheck, Smartphone, Target, UserX, Zap, MonitorPlay } from "lucide-react";
import { Helmet } from "react-helmet-async"; 

const FreeGames = () => {

  useEffect(() => {
  import("aos").then((AOS) => {
    AOS.default.init({
      duration: 500,
      once: true,
      offset: 80,
      easing: "ease-out-cubic",
    });
  });
}, []);

// 🚀 EXPANDED CONTENT FOR LOW CONTENT QUALITY FIX
const featuresList = [
  { 
    title: "100% Free Forever", 
    desc: "Enjoy unlimited access to our entire massive library without ever opening your wallet. There are absolutely no hidden costs, annoying paywalls, microtransactions, or premium subscriptions. Every single title on our platform is completely free to play online forever, ensuring entertainment is accessible to all." 
  },
  { 
    title: "Instant Load Times", 
    desc: "Powered by state-of-the-art HTML5 technology and highly optimized cloud servers, our browser games load in mere milliseconds. Jump straight into the action with zero waiting times and no frustrating lag, whether you are on a high-speed fiber connection or a standard mobile network." 
  },
  { 
    title: "Huge Genre Variety", 
    desc: "Whether you crave the adrenaline of high-speed 3D racing, the competitive spirit of intense multiplayer action, or the mental workout of brain-teasing logic puzzles, our massive catalog has something for everyone. We constantly update our categories so you can always discover and play fresh online games." 
  },
  { 
    title: "Safe & Secure Hub", 
    desc: "Your digital safety and privacy are our top priorities. We conduct rigorous daily malware scans and strict content moderation to ensure a highly secure, virus-free, and family-friendly online gaming experience. By playing directly in your browser, you never risk downloading malicious software." 
  },
  { 
    title: "No Account Needed", 
    desc: "We respect your privacy and value your time. You can play free online instantly without the frustrating hassle of creating an account, remembering complex passwords, confirming email addresses, or handing over your personal data to third parties." 
  },
  { 
    title: "Mobile Friendly UI", 
    desc: "Experience true, seamless cross-platform entertainment. Our highly responsive web design ensures that all our free games run flawlessly on mobile smartphones (iOS & Android), iPads, tablets, and desktop PCs, featuring dynamically adjusting layouts and perfect touch-swipe controls." 
  }
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Why Play Free Games at Game Zone Hub?",
  "itemListElement": featuresList.map((feature, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": feature.title,
    "description": feature.desc
  }))
};


  return (
    <section 
      className="relative bg-gradient-to-b lg:px-8 from-[#0b1120] to-[#141a33] sm:pt-[80px] pt-10 text-white sm:pb-16 pb-10"
    >
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4">

        <div className="text-center sm:mb-16 mb-10">
            <h2 
            className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold mb-6"
            data-aos="fade-down"
            data-aos-delay="100"
            >
            Why Play <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Free Games</span> at Game Zone Hub?
            </h2>

            <p 
            className="sm:text-lg text-[16px] text-gray-300 max-w-4xl mx-auto leading-relaxed"
            data-aos="fade-up"
            data-aos-delay="200"
            >
            We are completely redefining the way you <strong>play online games</strong>. 
            Forget about annoying paywalls, lengthy software installations, and unsafe downloads that slow down your device. 
            <strong>Game Zone Hub</strong> offers a premium, lag-free gaming experience directly in your modern web browser. 
            Enjoy a safe, highly secure, and <strong>100% free</strong> environment to <strong>play free online games</strong> endlessly, anytime and anywhere in the world.
            </p>
        </div>

        {/* FEATURES GRID */}
        <ul className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-8 sm:mb-20 mb-10">
        
        <li data-aos="zoom-in-up" data-aos-delay="100" className="h-full">
          <div className="group bg-[#1e293b] h-full p-8 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(59,130,246,0.25)] hover:border-blue-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-500/20 group-hover:scale-110 transition-all duration-300">
                <Gift className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[0].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[0].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="200" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(234,179,8,0.25)] hover:border-yellow-500/50 flex flex-col items-center text-center">
             <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-yellow-500/20 group-hover:scale-110 transition-all duration-300">
                <Zap className="w-8 h-8 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[1].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[1].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="300" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(236,72,153,0.25)] hover:border-pink-500/50 flex flex-col items-center text-center">
             <div className="w-16 h-16 bg-pink-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-pink-500/20 group-hover:scale-110 transition-all duration-300">
                <Target className="w-8 h-8 text-pink-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[2].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[2].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="400" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(34,197,94,0.25)] hover:border-green-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-green-500/20 group-hover:scale-110 transition-all duration-300">
                <ShieldCheck className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[3].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
             {featuresList[3].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="500" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(168,85,247,0.25)] hover:border-purple-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-purple-500/20 group-hover:scale-110 transition-all duration-300">
                <UserX className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[4].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[4].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="600" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(34,211,238,0.25)] hover:border-cyan-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
                <Smartphone className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[5].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[5].desc}
            </p>
          </div>
        </li>

      </ul>

        {/* 🚀 EXPANDED MINI-ARTICLE FOR MORE SEO VALUE */}
        <div className="bg-[#0f172a] rounded-3xl p-8 sm:p-12 border border-white/5 shadow-2xl mb-12" data-aos="fade-up" data-aos-delay="300">
            <div className="flex flex-wrap items-center sm:justify-center gap-3 mb-6">
                <MonitorPlay className="text-blue-400 w-8 h-8" />
                <h3 className="text-2xl sm:text-3xl font-bold text-white">The Rise of Browser Gaming</h3>
            </div>
            <div className="space-y-4 text-gray-400 text-sm sm:text-base leading-relaxed text-center max-w-4xl mx-auto">
                <p>
                    The gaming industry has evolved drastically. You no longer need an expensive console or a high-end PC to experience premium digital entertainment. Thanks to the power of modern <strong>HTML5 and WebGL technologies</strong>, developers can now compile massive, graphically intense 3D games to run directly and natively inside web browsers like Google Chrome, Safari, and Firefox.
                </p>
                <p>
                    Game Zone Hub is at the forefront of this revolution. By eliminating the friction of downloads, installations, and forced account creations, we are democratizing access to high-quality games. Join millions of players worldwide who have already made our platform their daily go-to for instant, unblocked, and <strong>100% free online gaming</strong>.
                </p>
            </div>
        </div>

        {/* CALL TO ACTION */}
        <div 
          className="text-center sm:pb-8"
          data-aos="fade-up"
          data-aos-delay="400"
        >
          <Link 
            to="/games" 
            aria-label="Play Free Online Games"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 sm:px-12 px-8 sm:py-5 py-4 rounded-full sm:text-lg text-[16px] font-extrabold text-white shadow-[0_0_25px_rgba(59,130,246,0.4)] transition-transform transform hover:scale-105 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)]"
          >
            Start Playing Free Games Now
          </Link>
        </div>

      </div>
    </section>
  );
};

export default FreeGames;